/**
 * Premium Hero Slideshow Controller - Ultra Customizable Edition
 * Fully functional slide management with autoplay and progress bar
 */

class PremiumHeroSlider {
  constructor(element) {
    this.element = element;
    this.slidesContainer = element.querySelector('[data-hero-slides]');
    this.slides = Array.from(element.querySelectorAll('.premium-hero-slide'));
    this.dots = Array.from(element.querySelectorAll('.premium-hero-dot'));
    this.prevBtn = element.querySelector('[data-hero-prev]');
    this.nextBtn = element.querySelector('[data-hero-next]');
    this.progressBar = element.querySelector('[data-hero-progress]');
    
    this.currentIndex = 0;
    this.autoplayTimer = null;
    this.progressTimer = null;
    this.isTransitioning = false;
    this.progressWidth = 0;
    
    // Get settings
    this.autoplay = this.getDataAttribute('autoplay', true);
    this.autoplaySpeed = this.getDataAttribute('autoplay_speed', 5) * 1000;
    this.animationSpeed = this.getDataAttribute('animation_speed', 800);
    
    this.init();
  }
  
  init() {
    if (this.slides.length <= 1) return;
    
    this.bindEvents();
    
    if (this.autoplay) {
      this.startAutoplay();
    }
    
    // Pause autoplay on hover
    this.element.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.element.addEventListener('mouseleave', () => {
      if (this.autoplay) this.resumeAutoplay();
    });
    
    // Visibility change handler
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseAutoplay();
      } else if (this.autoplay) {
        this.resumeAutoplay();
      }
    });
  }
  
  bindEvents() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prev());
    }
    
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.next());
    }
    
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });
    
    document.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.prev();
      if (e.key === 'ArrowRight') this.next();
    });
    
    this.addSwipeSupport();
  }
  
  goToSlide(index) {
    if (this.isTransitioning) return;
    if (index === this.currentIndex) return;
    
    this.isTransitioning = true;
    
    this.slides[this.currentIndex].classList.remove('active');
    if (this.dots[this.currentIndex]) {
      this.dots[this.currentIndex].classList.remove('active');
    }
    
    this.currentIndex = index;
    
    this.slides[this.currentIndex].classList.add('active');
    if (this.dots[this.currentIndex]) {
      this.dots[this.currentIndex].classList.add('active');
    }
    
    if (this.autoplay) {
      this.stopAutoplay();
      this.startAutoplay();
    }
    
    setTimeout(() => {
      this.isTransitioning = false;
    }, this.animationSpeed);
  }
  
  next() {
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this.goToSlide(nextIndex);
  }
  
  prev() {
    const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.goToSlide(prevIndex);
  }
  
  startAutoplay() {
    this.stopAutoplay();
    this.progressWidth = 0;
    
    if (this.progressBar) {
      this.updateProgressBar();
    }
    
    this.autoplayTimer = setTimeout(() => {
      this.next();
    }, this.autoplaySpeed);
  }
  
  updateProgressBar() {
    if (!this.progressBar) return;
    
    const step = 100 / (this.autoplaySpeed / 50);
    
    this.progressTimer = setInterval(() => {
      this.progressWidth += step;
      
      if (this.progressWidth >= 100) {
        this.progressWidth = 100;
        clearInterval(this.progressTimer);
      }
      
      this.progressBar.style.width = this.progressWidth + '%';
    }, 50);
  }
  
  stopAutoplay() {
    if (this.autoplayTimer) {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }
    
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = null;
    }
    
    this.progressWidth = 0;
    if (this.progressBar) {
      this.progressBar.style.width = '0%';
    }
  }
  
  pauseAutoplay() {
    if (this.autoplayTimer) {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }
    
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = null;
    }
  }
  
  resumeAutoplay() {
    if (!this.autoplay) return;
    
    const remainingTime = this.autoplaySpeed * (1 - this.progressWidth / 100);
    
    if (this.progressBar) {
      this.progressTimer = setInterval(() => {
        this.progressWidth += 100 / (remainingTime / 50);
        
        if (this.progressWidth >= 100) {
          this.progressWidth = 100;
          clearInterval(this.progressTimer);
        }
        
        this.progressBar.style.width = this.progressWidth + '%';
      }, 50);
    }
    
    this.autoplayTimer = setTimeout(() => {
      this.next();
    }, remainingTime);
  }
  
  addSwipeSupport() {
    let touchStartX = 0;
    let touchEndX = 0;
    
    this.slidesContainer.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });
    
    this.slidesContainer.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    }, { passive: true });
    
    this.handleSwipe = () => {
      const swipeThreshold = 50;
      const diff = touchStartX - touchEndX;
      
      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          this.next();
        } else {
          this.prev();
        }
      }
    };
  }
  
  getDataAttribute(key, defaultValue) {
    const sectionSettings = this.element.closest('[data-section-settings]');
    if (sectionSettings) {
      const value = sectionSettings.getAttribute(`data-${key}`);
      if (value !== null) {
        return value === 'true' ? true : value === 'false' ? false : parseFloat(value) || value;
      }
    }
    return defaultValue;
  }
  
  destroy() {
    this.stopAutoplay();
  }
}

// Initialize all hero sliders
document.addEventListener('DOMContentLoaded', () => {
  const heroSliders = document.querySelectorAll('.premium-hero-section');
  
  heroSliders.forEach((slider) => {
    new PremiumHeroSlider(slider);
  });
});

// Shopify theme editor support
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const slider = event.target.querySelector('.premium-hero-section');
    if (slider) {
      new PremiumHeroSlider(slider);
    }
  });
  
  document.addEventListener('shopify:section:unload', (event) => {
    const slider = event.target.querySelector('.premium-hero-section');
    if (slider && slider.heroSlider) {
      slider.heroSlider.destroy();
    }
  });
}